import React, { useEffect, useState } from 'react';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import { useForm } from 'react-hook-form';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { prepareFilters } from 'helpers/filters';
import { useTranslations } from 'hooks/useTranslations';
import { useLang } from 'models/langContext';
import { EDictionaryTypes } from 'models/dictionary';
import { useConfigurationLicence } from 'pages/LicenceConfigurationPage/hooks';
import { FormMultiSelect } from 'components/molecules/FormMultiSelect';

export interface ISearchCasesFormValues {
  caseId: string;
  statusUniqueId: string[];
}

export const initialCasesSearchFormValues: ISearchCasesFormValues = {
  caseId: '',
  statusUniqueId: [],
};

export enum ESearchCasesFormLabelsTypes {
  caseId = 'cases.search.caseId.label',
  statusUniqueId = 'cases.search.status.label',
}

interface ICasesSearchForm {
  onSubmit: (arg: ISearchCasesFormValues) => void;
  isSearchButtonDisabled: boolean;
}

const CasesSearchForm = ({ onSubmit, isSearchButtonDisabled = false }: ICasesSearchForm) => {
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ISearchCasesFormValues>({
    defaultValues: initialCasesSearchFormValues,
  });

  const [values, setValues] = useState<ISearchCasesFormValues>(initialCasesSearchFormValues);
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const { weaponLicenceCaseStatus, loadDictionaries } = useConfigurationLicence();

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as ISearchCasesFormValues);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    if (name) {
      reset({ ...getValues(), [name]: '' });
    } else {
      reset(initialCasesSearchFormValues);
    }
    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.weaponLicenceCaseStatus]);
    })();
  }, [selectedLanguage]);

  return (
    <SearchWrapper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container columnSpacing={2} sx={{ justifyContent: 'space-between' }}>
          <Grid size={{ xs: 12, md: 4, sm: 6 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="caseId"
              label={t(ESearchCasesFormLabelsTypes.caseId)}
              setValue={setValue}
              control={control}
              errors={errors}
              withValidation={false}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, sm: 6 }}>
            <FormMultiSelect
              options={mapDictionaryToOptionProp(weaponLicenceCaseStatus)}
              name="statusUniqueId"
              label={t(ESearchCasesFormLabelsTypes.statusUniqueId)}
              canBeEmpty
              setValue={setValue}
              control={control}
              errors={errors}
              withValidation={false}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Button
              variant={EButtonVariants.contained}
              size={EButtonSizes.small}
              label={t('legalEntities.individual.accordion.search.button')}
              disabled={isSearchButtonDisabled}
              type="submit"
              fullWidth
              sx={{ mt: 3.5 }}
            />
          </Grid>
        </Grid>
      </form>
      <SelectedFilters
        name="cases"
        values={values}
        labels={ESearchCasesFormLabelsTypes}
        handleDelete={handleResetField}
        noPreset
        showSaveFilters={false}
        dictionaries={{
          statusUniqueId: prepareFilters(weaponLicenceCaseStatus, 'uniqueId', 'name'),
        }}
        translationKeys={{
          selectedCriteria: 'searchForLegalEntityAncillary.selectedCriteria.label',
        }}
        saveRouterStateAfterClearAll={false}
      />
    </SearchWrapper>
  );
};

export { CasesSearchForm };
